import type { RemovableTokenTone } from 'components/atomic/atoms/removable-token';
import type { ParsedSearchFilters } from 'utils/natural-language-filter-parser/types';

/** Location fields the parser resolves. These describe where to search, not a `Filter` value. */
export const LOCATION_FIELDS = ['neighbourhood', 'city', 'province', 'country'] as const;
export type SmartSearchLocationField = typeof LOCATION_FIELDS[number];

export type SmartSearchLocation = Partial<Record<SmartSearchLocationField, string>>;

/**
 * One understood value from the query.
 *
 * `id` is unique within a parse and is what the UI passes back on removal.
 * Some ids cover a pair of parsed keys (`price` → `priceMin` + `priceMax`) and
 * some are per-value (`homeType:condo`); {@link removeTokenFromFilters} owns
 * the mapping back to {@link ParsedSearchFilters} keys.
 */
export interface SmartSearchTokenModel {
  id: string;
  /** Reads as a complete phrase on its own (e.g. "3+ Beds", "Under $1M"). */
  label: string;
  /** Small leading glyph, e.g. a pin on location chips. */
  icon?: string;
  tone?: RemovableTokenTone;
  isLocation?: boolean;
}

export type SmartSearchStatus = 'idle' | 'parsing' | 'ready' | 'error';

export interface SmartSearchTagMatch {
  tagName: string;
  maxMatchSim: number;
}

export interface SmartSearchParseState {
  status: SmartSearchStatus;
  /** Filters understood from the query, minus any the user removed. */
  filters: ParsedSearchFilters;
  tokens: SmartSearchTokenModel[];
  location: SmartSearchLocation;
  /** Leftover intent the parser could not account for. */
  unparsed: string | null;
  /** Overall parse confidence (0–1), or null when the parser omits it. */
  confidence: number | null;
}
